/**
 * Which fabrics a plan includes, and what the paid line actually is.
 *
 * The policy from docs/decisions/fabric-picker-and-gating.md, in one place and
 * pure, so it can be read and tested without a browser.
 *
 * Two rules:
 *
 *   **Free gets eight fabrics, not zero.** A library is a storefront, and one
 *   nobody can enter sells nothing — "pattern engine ❌" meant a free user never
 *   saw what the feature did, so the upgrade was an abstraction rather than a
 *   want. Eight spanning every group is enough to finish a garment.
 *
 *   **Tint is the paid line.** It turns 28 fabrics into 28 weaves in any
 *   colour, which is worth more than any number of extra tiles; it is
 *   impossible to work around; and a free user meets it while holding something
 *   they already like rather than in the abstract.
 *
 * Everything stays *previewable* regardless — see the picker. The taste is
 * their design on their avatar in the fabric they just pointed at, and it costs
 * nothing to give away because no work has been spent when the wall arrives.
 */
import type { Tier } from "../../shared/tiers";

/**
 * The free set: one strong representative of every group, and three patterns.
 *
 * Chosen against the panel-scale measurement rather than by taste. Patterns get
 * three because they are the only things that keep their identity at 128px
 * un-tinted, so they are what a free user can actually make something with; the
 * plain weaves are one each because, un-tinted, one flat colour is much like
 * another and tint is the upgrade.
 */
export const FREE_FABRICS: readonly string[] = [
  "gingham_check", // pattern — the best-reading tile in the library
  "fabric_pattern_07-col1", // pattern — red plaid
  "curly_teddy_checkered", // pattern — soft check
  "denim_fabric", // denim — the most-requested fabric there is
  "brown_leather", // leather
  "jersey_melange", // knit
  "ribbed_corduroy", // woven
  "crepe_satin", // smooth
];

const FREE = new Set(FREE_FABRICS);

/** Is this fabric outside the plan? */
export function isFabricLocked(id: string, tier: Tier): boolean {
  if (tier !== "free") return false;
  return !FREE.has(id);
}

/**
 * Can this plan recolour a fabric?
 *
 * The single paid line, and deliberately not a count of anything — a limit you
 * can exhaust invites rationing, and rationing a creative tool is how people
 * decide it is not for them.
 */
export function canTint(tier: Tier): boolean {
  return tier !== "free";
}

/** What to say when a locked fabric is chosen. Names the thing they wanted. */
export function lockedMessage(name: string): string {
  return `${name} is part of Creator`;
}
